import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, CreditCard, ShieldAlert, ClipboardList, Receipt } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { OpenRequestsPanel } from '@/components/dashboard/open-requests-panel'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DecideApprovalModal } from '@/components/approvals/decide-approval-modal'
import { useApprovals } from '@/hooks/use-approvals'
import { useMemberArrears } from '@/hooks/use-billing'
import { useCreditBalances } from '@/hooks/use-credits'
import { useServiceRequests } from '@/hooks/use-requests'
import type { ApprovalRequest } from '@riverpark/types'

const ATTENTION_LIMIT = 5

function formatDateTime(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString('en-KE', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function StatCard({
  to,
  icon: Icon,
  label,
  value,
  hint,
}: {
  to: string
  icon: typeof ShieldAlert
  label: string
  value: string
  hint?: string
}) {
  return (
    <Link
      to={to}
      className="rounded-card border border-border-2 bg-card p-4 transition-colors hover:border-green/40"
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon size={15} />
        <p className="font-sans text-[11px] font-medium uppercase tracking-[0.1em]">{label}</p>
      </div>
      <p className="mt-2 font-serif text-2xl text-ink">{value}</p>
      {hint && <p className="mt-0.5 font-sans text-xs text-muted-foreground">{hint}</p>}
    </Link>
  )
}

export interface StaffDashboardPageProps {
  basePath: string
  showCreditsWidget?: boolean
  showOpenRequestsPanel?: boolean
}

export function StaffDashboardPage({
  basePath,
  showCreditsWidget = false,
  showOpenRequestsPanel = false,
}: StaffDashboardPageProps) {
  const { data: approvalsData, isLoading: approvalsLoading } = useApprovals('pending')
  const { data: requestsData, isLoading: requestsLoading } = useServiceRequests({ limit: 100 })
  const { data: arrearsData } = useMemberArrears({ limit: 1 })
  const { data: creditsData } = useCreditBalances()
  const [target, setTarget] = useState<ApprovalRequest | null>(null)

  const pending = (approvalsData?.data ?? []).slice(0, ATTENTION_LIMIT)
  const openRequests = (requestsData?.data ?? []).filter(
    (r) => r.status === 'open' || r.status === 'in_progress',
  )
  const arrearsTotal = arrearsData?.meta.total ?? 0

  return (
    <PageShell>
      <PageHeader label="Overview" title="Dashboard" description="What's waiting on you today." />

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <StatCard
          to={`${basePath}/requests`}
          icon={ClipboardList}
          label="Open requests"
          value={String(openRequests.length)}
        />
        <StatCard
          to={`${basePath}/billing`}
          icon={Receipt}
          label="Members in arrears"
          value={String(arrearsTotal)}
        />
        {showCreditsWidget && creditsData && (
          <StatCard
            to="/admin/settings"
            icon={CreditCard}
            label="SMS / Email credits"
            value={`${creditsData.data.sms} / ${creditsData.data.email}`}
            hint="Remaining sends"
          />
        )}
      </div>

      {showOpenRequestsPanel && (
        <OpenRequestsPanel
          basePath={basePath}
          requests={openRequests}
          isLoading={requestsLoading}
        />
      )}

      <div className="rounded-card border border-border-2 bg-card">
        <div className="flex items-center justify-between gap-2 border-b border-border-2 px-5 py-3">
          <div className="flex items-center gap-2">
            <ShieldAlert size={16} className="text-muted-foreground" />
            <h2 className="font-serif text-lg text-ink">Needs your attention</h2>
          </div>
          <Link
            to={`${basePath}/approvals`}
            className="flex items-center gap-1 font-sans text-xs font-medium text-muted-foreground hover:text-primary"
          >
            View all
            <ArrowRight size={12} />
          </Link>
        </div>

        {approvalsLoading ? (
          <p className="px-5 py-6 font-sans text-sm text-muted-foreground">Loading…</p>
        ) : pending.length === 0 ? (
          <p className="px-5 py-6 font-sans text-sm text-muted-foreground">
            Nothing pending — you're all caught up.
          </p>
        ) : (
          <ul className="divide-y divide-border-2">
            {pending.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 px-5 py-3.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="pending">pending</Badge>
                    <p className="truncate font-sans text-sm font-medium text-ink">{r.summary}</p>
                  </div>
                  <p className="mt-1 font-sans text-xs text-muted-foreground">
                    Requested by {r.initiatedByName} · {formatDateTime(r.createdAt)}
                  </p>
                </div>
                <Button variant="secondary" size="sm" onClick={() => setTarget(r)}>
                  Review
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {target && <DecideApprovalModal request={target} onClose={() => setTarget(null)} />}
    </PageShell>
  )
}
